import {Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {NgControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {Subscription} from 'rxjs';
import {HtmlEditModalComponent} from "./edit-modal/html-edit-modal.component";

@Directive({
    selector: 'quill-editor[appEditableQuillHtml]', // Target quill-editor specifically
    standalone: true, // For Angular 14+ standalone components/directives
    // If not standalone, declare and export in a module
})
export class EditableQuillHtmlDirective implements OnInit, OnDestroy {
    private button: HTMLButtonElement | null = null;
    private valueChangesSubscription: Subscription | undefined;

    // If you need to pass config to the directive, e.g. button text
    @Input('appEditableQuillHtmlButtonText') buttonText: string = 'Edit HTML';

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private ngControl: NgControl, // Injects NgModel, NgFormControl, etc.
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        if (!this.ngControl || !this.ngControl.control) {
            console.warn('EditableQuillHtmlDirective requires ngModel or formControlName on the quill-editor.');
            return;
        }

        this.createEditButton();

        // Optional: If the ngModel value can be changed programmatically elsewhere
        // and you want the button to always reflect the latest state before opening modal.
        // This is often not strictly necessary if the modal always reads fresh on open.
        // this.valueChangesSubscription = this.ngControl.valueChanges?.subscribe(value => {
        //   // console.log('Value changed:', value);
        // });
    }

    // ... inside EditableQuillHtmlDirective ...

    private createEditButton(): void {
        this.button = this.renderer.createElement('button');
        this.renderer.setProperty(this.button, 'textContent', this.buttonText);
        // Add a more specific class for styling
        this.renderer.addClass(this.button, 'quill-edit-html-button');

        // Ensure the quill-editor element itself can be a positioning context
        if (getComputedStyle(this.el.nativeElement).position === 'static') {
            this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
        }

        // Append the button as a direct child of the <quill-editor> element
        this.renderer.appendChild(this.el.nativeElement, this.button);

        this.renderer.listen(this.button, 'click', (event: Event) => {
            event.stopPropagation(); // Prevent Quill from processing the click if it's inside its area
            this.openHtmlEditModal();
        });
    }

    private openHtmlEditModal(): void {
        if (!this.ngControl || !this.ngControl.control) return;

        const currentHtml = this.ngControl.control.value;

        const dialogRef = this.dialog.open(HtmlEditModalComponent, {
            width: '80vw', // Or '800px', etc.
            maxWidth: '95vw',
            data: { html: currentHtml },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result !== undefined && result !== null) { // Check if user didn't just cancel
                // Update the ngModel value
                this.ngControl.control?.setValue(result);
                this.ngControl.control?.markAsDirty(); // Optional: if you use form states
                this.ngControl.control?.markAsTouched(); // Optional
            }
        });
    }

    ngOnDestroy(): void {
        if (this.button && this.button.parentNode) {
            this.renderer.removeChild(this.button.parentNode, this.button);
        }
        this.valueChangesSubscription?.unsubscribe();
    }
}
